import axios from "axios";
import { devError } from "./log";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth uses httpOnly cookies as the primary mechanism. As a fallback for
// browsers that block third-party / cross-site cookies (Safari ITP, private
// modes, some mobile browsers), we also keep the access_token in sessionStorage
// and attach it as `Authorization: Bearer ...`. This NEVER writes to localStorage
// so the token disappears when the tab closes. The backend accepts either.
const TOKEN_SS_KEY = "vendeia_session_token";

// One-time cleanup: we used to store a JWT in localStorage. If a user still has
// a stale entry from an older build, wipe it — it's no longer used and the
// interceptor below may otherwise attach a dead token.
try {
  if (typeof window !== "undefined" && window.localStorage?.getItem("vendeia_token")) {
    window.localStorage.removeItem("vendeia_token");
  }
} catch (e) {
  // ignore (private mode, storage disabled)
}

export const getSessionToken = () => {
  try {
    return window.sessionStorage?.getItem(TOKEN_SS_KEY) || null;
  } catch {
    return null;
  }
};

export const setSessionToken = (token) => {
  try {
    if (token) window.sessionStorage.setItem(TOKEN_SS_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_SS_KEY);
  } catch (e) {
    devError("sessionStorage write failed", e);
  }
};

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Attach Bearer fallback if we have one. The browser may also send the
  // httpOnly cookie — backend accepts either and the header takes precedence.
  const t = getSessionToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Only kick the user back to /login when the auth check itself fails.
    // A 401 on, say, /api/sucursales due to a transient network blip shouldn't
    // nuke an active session.
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && url.includes("/auth/me")) {
      setSessionToken(null);
      const path = window.location.pathname;
      // Public routes that an unauthenticated user is *expected* to visit
      // (login, public product page, password recovery). Don't bounce them.
      const PUBLIC = ["/login", "/p/", "/forgot-password", "/reset-password"];
      if (!PUBLIC.some((p) => path.startsWith(p))) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const SUCURSAL_KEY = "vendeia_sucursal_id";

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = String(result).split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const fmt = (n, currency = "MXN") => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  try {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${Number(n).toLocaleString("es-MX")}`;
  }
};

export const formatApiError = (err, fallback = "Algo salió mal") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
